import { afterNextRender, Component, untracked, computed, effect, ElementRef, inject, linkedSignal, signal, TemplateRef, ViewChild, viewChild } from '@angular/core';
import { JobStateService } from './job-state.store';
import { ViewerSettingsService } from './viewer-settings.store';
import { debounceTime, distinctUntilChanged, firstValueFrom, fromEvent, Subject, Subscription, take } from 'rxjs';
import { CachedProjectService } from '../../../shared/services/cached-project.service';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzMessageService } from 'ng-zorro-antd/message';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BatchScreenshotService, BatchTarget } from './batch-screenshot.service';
import { JobDetail } from '../../../models/jobDetail';

@Component({
  selector: "app-model-new",
  templateUrl: "./model-new.component.html",
  styleUrl: "./model-new.component.less",
  standalone: false,
})
export class ModelNewComponent {
  // services
  protected jobStateService = inject(JobStateService)
  protected viewerSettings = inject(ViewerSettingsService)
  private cachedProjectService = inject(CachedProjectService)
  private nzNotificationService = inject(NzNotificationService)
  private batchScreenshotService = inject(BatchScreenshotService)
  private nzMessageService = inject(NzMessageService)

  // current job
  protected currentJob = computed(() => this.jobStateService.jobData()); // signal from service
  protected maxInline = computed(() =>
    this.currentJob().x2_is_inline
      ? this.currentJob().modelGrid.nx2
      : this.currentJob().modelGrid.nx1,
  );
  protected maxCrossline = computed(() =>
    this.currentJob().x2_is_inline
      ? this.currentJob().modelGrid.nx1
      : this.currentJob().modelGrid.nx2,
  );
  protected maxDepth = computed(() => this.currentJob().modelGrid.nx3);
  protected maxIterations = computed(() => this.currentJob().iterationsComplete);

  protected readonly FUNCTIONAL_COLORS = {
      AWI: "#F8DC6F",
      FWI: "#EE865B",
      RWI: "#4295A5",
  };
  protected readonly TYPE_COLORS: Record<string, string> = {
    'QC':       'oklch(90.5% 0.182 98.111)',
    'SC':       'oklch(58.6% 0.253 17.585)',
    'standard': '#2196F3',
    'RWI':      'oklch(75% 0.183 55.934)',
    'FM':  'oklch(69.6% 0.17 162.48)'
  };

  // job comment — savedComments tracks the persisted value so we know when edits are unsaved
  protected comments = linkedSignal(() => this.currentJob()?.comments ?? "");
  private savedComments = linkedSignal(() => this.currentJob()?.comments ?? "");
  protected commentDirty = computed(() => (this.comments() ?? "") !== (this.savedComments() ?? ""));
  protected commentEditing = signal(false);
  protected commentSaving = signal(false);
  private commentInput = viewChild<ElementRef<HTMLTextAreaElement>>("commentInput");

  protected bottomTabsHeight = signal<number>(0);
  private resizeObserver?: ResizeObserver;
  private slicesResizeObserver?: ResizeObserver;
  private widthChangeSubscription: Subject<number> = new Subject()

  private sliceSettingsTop = viewChild<ElementRef<HTMLDivElement>>('targetElement');
  private slicesContainer = viewChild<ElementRef<HTMLDivElement>>('ssDiv');
  private observer?: IntersectionObserver;
  protected isAlternateVisible = signal(false);
  protected widthXL: number;
  protected widthIL: number;
  protected isMobile: boolean | undefined;
  protected isLandscape = false;
  
  scrollToAnchor(anchor: string) {
    const element = document.getElementById(anchor);
    if (element) {
      const headerOffset = 70; // Change this to whatever looks best
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  }

  onScreenshotHandler(slice: string = null){
    // capture logic (incl. VP/depth hiding and file naming) lives in the
    // batch screenshot service so the batch runner and these buttons share it
    const target = (slice?.toLowerCase() ?? 'full') as BatchTarget;
    // slide / fixed-width frames re-lay the viewers out before capturing, which takes a moment
    const loading = this.nzMessageService.loading('Rendering screenshot…', { nzDuration: 0 }).messageId
    this.batchScreenshotService.captureTarget(target)
      .then(({ name, dataUrl }) => this.batchScreenshotService.downloadDataUrl(name, dataUrl))
      .catch((error) => {
        console.error("Error generating screenshot:", error);
      })
      .finally(() => this.nzMessageService.remove(loading));
  }

  checkForNewIteration(job: JobDetail) {
    firstValueFrom(this.cachedProjectService.checkForNewIterations(job.id)).then((res) => {
      if ("iterationsAvailable" in res && res["iterationsAvailable"]){
        this.jobStateService.reparseJob(job);
      }
    })
    .catch((err) => {
      console.log(
        "Error when checking for new iterations",
        err.error?.error || err || "There was an unexpected error",
      );
    });
  }

  nullifyLog(){
    if (this.jobStateService.selectedLog()){
      this.jobStateService.selectedLog.set(null)
    }
  }

  startCommentEdit() {
    this.commentEditing.set(true);
    setTimeout(() => this.commentInput()?.nativeElement.focus());
  }

  cancelCommentEdit() {
    this.comments.set(this.savedComments());
    this.commentEditing.set(false);
  }

  saveComment() {
    if (!this.commentDirty() || this.commentSaving()) return;
    const job = this.currentJob();
    this.commentSaving.set(true);
    this.cachedProjectService.updateJobDetails({
      id: job.id,
      name: job.name,
      comments: this.comments(),
      owner: job.owner?.id,
    }).pipe(take(1)).subscribe({
      next: () => {
        this.savedComments.set(this.comments());
        this.commentSaving.set(false);
        this.commentEditing.set(false);
        this.cachedProjectService.clearProjectsCache();
        this.cachedProjectService.clearJobDetailCache([job.id]);
      },
      error: (err) => {
        this.commentSaving.set(false);
        this.nzNotificationService.error(
          "Error saving comment",
          err.error?.error || err?.message || "An unexpected error occurred",
        );
      },
    });
  }
  
  
  constructor() {
    // 'afterNextRender' is the safest way to handle DOM-reliant logic in Angular
    afterNextRender(() => {
      this.isMobile = this.checkIsMobile();
      this.initObserver();
      this.initTabsResizeObserver();
      this.initSlicesResizeObserver();
    });

    fromEvent(window, 'resize').pipe(takeUntilDestroyed(), debounceTime(150)).subscribe(() => {
      this.isMobile = this.checkIsMobile();
    });

    effect(() => {
      const job = this.currentJob();
      if (job) untracked(() => this.checkForNewIteration(job));
    })

    // the width change subscription is only used to sort of leverage the resizeObserver, 
    // cause that emits values on pretty much all dimension changes but we care only about the width
    this.widthChangeSubscription.pipe(takeUntilDestroyed(), debounceTime(50), distinctUntilChanged()).subscribe({
      next: (width) => {
        // caclulate the size ratios
        let modelGrid = this.jobStateService.jobData().modelGrid
        let x2IsInline = this.jobStateService.jobData().x2_is_inline
        let ratioIL = (x2IsInline ? modelGrid.nx1 : modelGrid.nx2) / (modelGrid.nx1 + modelGrid.nx2)
        let ratioXL = 1 - ratioIL
        // -5 from both to get -10 i.e. space between the slices
        let widthIL = ratioIL * width
        let widthXL = ratioXL * width
        
        // makes it so both must atleast be 400 pixels wide
        // but also make sure that the other big one isn't TOO big
        if (widthIL < widthXL){
          widthIL = Math.max(widthIL, 400)
          // the second one (bigger slice) shouldn't be TOO big in this scenario
          // so we take the min of the width calculated for the slice, and the 'available' width after the 5px spacing, and the width of the smaller slice
          widthXL = Math.min(widthXL, width - widthIL) - 10
        }
        else{
          widthXL = Math.max(widthXL, 400)
          widthIL = Math.min(widthIL, width - widthXL) - 10
        }
        
        this.widthIL = widthIL
        this.widthXL = widthXL
      }
    })
  }

  private initSlicesResizeObserver() {
    const element = this.slicesContainer().nativeElement
    if (element){
      this.slicesResizeObserver = new ResizeObserver((entries)=> {
        let width = entries[0].contentRect.width
        this.widthChangeSubscription.next(width)
      })
      this.slicesResizeObserver.observe(element)
    }
  }

  private initTabsResizeObserver(){
    // this for the offset of the bottom slice settings that transitions up
    // ite basically just he height of the bottom job snav
    // it changes when hidden / showing but also if any favourites are selected
    const element = document.getElementById('job-tabs-bottom');
    if (element) {
      this.resizeObserver = new ResizeObserver((entries) => {
        this.bottomTabsHeight.set(entries[0].contentRect.height);
      });
      this.resizeObserver.observe(element);
    }

  }

  private checkIsMobile(): boolean {
    this.isLandscape = window.innerWidth > window.innerHeight;
    return /Mobile/.test(navigator.userAgent) ||
      window.innerWidth <= 600 ||
      (window.innerHeight <= 600 && window.innerWidth > window.innerHeight);
  }

  private initObserver() {
    // this observer is to show the bottom slice settings once the top slice settings are gone
    const element = this.sliceSettingsTop()?.nativeElement;
    if (!element) return;

    this.observer = new IntersectionObserver(([entry]) => {
      // If isIntersecting is false, it means the div has left the screen
      this.isAlternateVisible.set(!entry.isIntersecting);
    }, {
      threshold: 0, // Trigger as soon as the last pixel leaves
      rootMargin: '-130px 0px 0px 0px' 
    });

    this.observer.observe(element);
  }

  ngOnDestroy() {
    // clean up
    this.observer?.disconnect();
    this.resizeObserver?.disconnect()
    this.slicesResizeObserver?.disconnect()
  }
}

export const stripDash = function (str: string) {
  if (str.startsWith("-")) {
    return stripDash(str.substring(1));
  } else if (str.endsWith("-")) {
    return stripDash(str.substring(0, str.length - 1));
  }
  return str;
};